// client/src/pages/courses.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import BackButton from '../../components/customs/backButton';
import ProfIXChatbot from '../../components/chatBot/ProfIXChatbot';
import { GlobalContext } from '../../context/provider';
import useCourse from '../../hooks/useCourse';
import useStudent from '../../hooks/useStudent';

import '../../styles/globalstyles.css';
import './courses.css';

const Courses = () => {
    const navigate = useNavigate();
    const { 
        authState: { authData }
    } = useContext(GlobalContext);
    
    const { 
        getAllCourses,
        getCourseById
    } = useCourse();

    const {
        getStudentByLoginId,
        enrollCourse,
        addToCart,
        getCart
    } = useStudent();

    const [activeTab, setActiveTab] = useState('available');
    const [isLoading, setIsLoading] = useState(true);
    const [availableCourses, setAvailableCourses] = useState([]);
    const [enrolledCourses, setEnrolledCourses] = useState([]);
    const [filteredCourses, setFilteredCourses] = useState([]);
    
    // Search and filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedLevel, setSelectedLevel] = useState('all');
    
    // Modal states
    const [showCourseModal, setShowCourseModal] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [isEnrolling, setIsEnrolling] = useState(false);

    const [expandedLessons, setExpandedLessons] = useState({});
    const toggleLesson = (lessonId) => {
        setExpandedLessons(prev => ({ ...prev, [lessonId]: !prev[lessonId] }));
    };

    useEffect(() => {
        loadPage();
    }, []);

    useEffect(() => {
        filterCourses();
    }, [searchQuery, selectedCategory, selectedLevel, availableCourses, activeTab]);

    const loadPage = async () => {
        setIsLoading(true);
        try {
            const allCoursesResponse = await getAllCourses();
            const studentResponse = await getStudentByLoginId(authData.id);
            const cartResponse = await getCart(authData.id);
            
            if (allCoursesResponse.success) {
                const allCourses = allCoursesResponse.data || [];
                const publishedCourses = allCourses.filter(course => 
                    course.isPublished
                );
                const targetCourses = (authData.role === 'admin' && allCourses) || publishedCourses;
                const enrolledCourseIds = studentResponse.success 
                    ? (studentResponse.data?.courses || []).map(ec => ec.courseId)
                    : [];

                const cartCourseIds = cartResponse?.success        
                    ? (cartResponse.data?.items || []).map(ci => ci.courseId)
                    : [];
                
                const coursesWithEnrollmentStatus = targetCourses.map(course => ({
                // const coursesWithEnrollmentStatus = publishedCourses.map(course => ({
                    ...course,
                    isEnrolled: enrolledCourseIds.includes(course.courseId),
                    isInCart:   cartCourseIds.includes(course.courseId)
                }));
                
                setAvailableCourses(coursesWithEnrollmentStatus);
                setFilteredCourses(coursesWithEnrollmentStatus);
            }
        } catch (error) {
            console.error('Failed to load courses:', error);
            showNotification('Failed to load courses', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const filterCourses = () => {
        let filtered = availableCourses;
        
        if (searchQuery.trim()) {
            filtered = filtered.filter(course => 
                course.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                course.courseId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                course.instructorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                course.shortDescription?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }
        
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(course => course.category === selectedCategory);
        }
        
        if (selectedLevel !== 'all') {
            filtered = filtered.filter(course => course.level === selectedLevel);
        }
        
        setFilteredCourses(filtered);
    };

    const handleEnrollCourse = async (courseId) => {
        setIsEnrolling(true);
        try {
            const response = await enrollCourse(authData.id, courseId);
            if (response.success) {
                showNotification('Successfully enrolled in course!', 'success');
                setShowCourseModal(false);
                await loadPage();
            } else {
                showNotification(response.message || 'Enrollment failed', 'error');
            }
        } catch (error) {
            console.error('Enrollment error:', error);
            showNotification('Failed to enroll in course', 'error');
        } finally {
            setIsEnrolling(false);
        }
    };

    const handleAddToCart = async (course) => {
        try {
            const response = await addToCart(authData.id, {
                courseId: course.courseId,
                price: course.price || 0,
                currency: course.currency || 'USD'
            });
            if (response.success) {
                window.dispatchEvent(new Event('cartUpdated'));
                showNotification('Course added to cart!', 'success');

                setFilteredCourses(prev =>
                    prev.map(c => c.courseId === course.courseId ? { ...c, isInCart: true } : c)
                );
                setAvailableCourses(prev =>
                    prev.map(c => c.courseId === course.courseId ? { ...c, isInCart: true } : c)
                );
            } else {
                showNotification(response.message || 'Failed to add to cart', 'error');
            }
        } catch (error) {
            console.error('Add to cart error:', error);
            showNotification('Failed to add to cart', 'error');
        }
    };

    const openCourseModal = (course) => {
        setSelectedCourse(course);
        setShowCourseModal(true);
    };

    const closeCourseModal = () => {
        setShowCourseModal(false);
        setSelectedCourse(null);
    };

    const showNotification = (message, type = 'info') => {
        const notification = document.createElement('div');
        notification.className = 'notification-toast';
        const bgColors = {
            success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            error: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            info: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
        };
        const icons = { success: '✅', error: '❌', info: 'ℹ️' };
        
        notification.style.background = bgColors[type] || bgColors.info;
        notification.innerHTML = `
            <span class="notification-icon">${icons[type]}</span>
            <span>${message}</span>
        `;
        document.body.appendChild(notification);
        setTimeout(() => {
            notification.style.animation = 'slideOut 0.3s ease-out forwards';
            setTimeout(() => document.body.removeChild(notification), 300);
        }, 3000);
    };

    const getUniqueCategories = () => {
        const categories = new Set();
        availableCourses.forEach(course => {
            if (course.category) categories.add(course.category);
        });
        return Array.from(categories);
    };

    const getUniqueLevels = () => {
        const levels = new Set();
        availableCourses.forEach(course => {
            if (course.level) levels.add(course.level);
        });
        return Array.from(levels);
    };

    const getLevelColor = (level) => {
        const colors = {
            'Beginner': 'level-beginner',
            'Intermediate': 'level-intermediate',
            'Advanced': 'level-advanced',
        };
        return colors[level] || 'level-beginner';
    };

    return (
        <div className="main-container">
            <div className="main-content">
                
                {/* Header */}
                <div className="header-row">
                    <ProfileHeader 
                        data={authData}
                        // title="📚 Course Catalog"
                        activePage="studentdashboard" 
                    />
                    <div className="headerMiddle">
                        <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
                    </div>
                    <SharedHeader 
                        notificationCount={0} 
                        icons={[]} 
                    />
                </div>

                {/* Toolbar */}
                <div className="ai-notebook-toolbar">
                    <div className="ai-notebook-toolbar-left">
                    <BackButton 
                        to="-1"
                        activeTab="management"
                        label="←"
                        bgColor="#6b7280"
                    />
                    <span className="ai-notebook-flow-name">
                        <span className="ai-notebook-lab-tag">📚 Course Catalog</span>
                        {/* <span className="ai-notebook-session-id">📚 Course: {courseInfo?.title} ({courseInfo?.courseId})</span> */}
                    </span>
                    </div>
                    {/* <div className="ai-notebook-toolbar-right">
                    </div> */}
                </div>

                {/* Search and Filter Bar */}
                <div className="courses-filter-bar">
                    <div className="search-box">
                        <span className="search-icon">🔍</span>
                        <input
                            type="text"
                            placeholder="Search by title, ID, or instructor..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                    </div>

                    <div className="filter-group">
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">All Categories</option>
                            {getUniqueCategories().map((category, idx) => (
                                <option key={idx} value={category}>{category}</option>
                            ))}
                        </select>

                        <select
                            value={selectedLevel}
                            onChange={(e) => setSelectedLevel(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">All Levels</option>
                            {getUniqueLevels().map((level, idx) => (
                                <option key={idx} value={level}>{level}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Content Area */}
                <div className="main-content-area">
                    {isLoading ? (
                        <div className="loading-container">
                            <div className="loader"></div>
                            <p>Loading courses...</p>
                        </div>
                    ) : (
                        <div className="courses-grid">
                            {filteredCourses.length > 0 ? (
                                filteredCourses.map((course, idx) => (
                                    <div key={idx} className={`course-card ${course.isEnrolled ? 'is-enrolled' : ''}`}>
                                        
                                        {/* Top accent bar with category color */}
                                        {/* <div className="course-card-accent"></div> */}
                                        {/* Course Image */}
                                        <div className="course-card-image">
                                            <img
                                                src={`/courseImages/${course.title}.png`}
                                                alt={course.title}
                                                className="course-card-img"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.classList.add('course-card-image--fallback');
                                                }}
                                            />
                                        </div>

                                        {/* Card Body */}
                                        <div className="course-card-body">

                                            {/* Title + Price row */}
                                            <div className="course-title-price-row">
                                                <h3 className="course-card-title">{course.title}</h3>
                                                <div className="course-price-block">
                                                    <span className="course-price-amount">
                                                        {parseFloat(course.price) === 0 ? 'Free' : `${course.price}`}
                                                    </span>
                                                    {parseFloat(course.price) > 0 && (
                                                        <span className="course-price-currency">{course.currency} / year</span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Description */}
                                            <p className="course-card-description">{course.shortDescription}</p>

                                            {/* Compact meta: instructor · duration · credits */}
                                            <div className="course-card-meta-row">
                                                <span className="meta-chip">
                                                    <span className="meta-chip-icon">👨‍🏫</span>
                                                    {course.instructorName || 'TBA'}
                                                </span>
                                                <span className="meta-divider">·</span>
                                                <span className="meta-chip">
                                                    <span className="meta-chip-icon">⏱️</span>
                                                    {course.duration || 'N/A'}
                                                </span>
                                                <span className="meta-divider">·</span>
                                                <span className="meta-chip">
                                                    <span className="meta-chip-icon">🎓</span>
                                                    {course.credits || 0} cr.
                                                </span>
                                            </div>

                                            {/* Tags row: category + level + courseId */}
                                            <div className="course-card-tags">
                                                <span className="tag tag-id">{course.courseId}</span>
                                                <span className="tag tag-category">{course.category}</span>
                                                <span className={`tag tag-level ${getLevelColor(course.level)}`}>{course.level}</span>
                                            </div>

                                        </div>

                                        {/* Card Footer */}
                                        <div className="course-card-footer">
                                            <button
                                                onClick={() => openCourseModal(course)}
                                                className="btn-view-details"
                                            >
                                                View Details
                                            </button>

                                            {course.isEnrolled ? (
                                                <span className="enrolled-badge">✅ Enrolled</span>
                                            ) : course.isInCart ? (
                                                <button className="btn-in-cart" disabled>
                                                    🛒 In Cart
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleAddToCart(course)}
                                                    className="btn-add-cart"
                                                >
                                                    🛒 Add to Cart
                                                </button>
                                            )}
                                        </div>

                                    </div>
                                ))
                            ) : (
                                <div className="no-courses-message">
                                    <h3>
                                        {activeTab === 'available' 
                                            ? '🔍 No available courses found' 
                                            : '📚 No enrolled courses yet'}
                                    </h3>
                                    <p>
                                        {activeTab === 'available'
                                            ? 'Try adjusting your search or filters'
                                            : 'Browse available courses to get started'}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Course Details Modal */}
            {showCourseModal && selectedCourse && (
                <div className="modal-overlay" onClick={closeCourseModal}>
                    <div className="modal-content-top modal-content-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-top">
                            <h3>📚 Course Details</h3>
                            <div style={{display: 'flex'}}>
                                {selectedCourse.isEnrolled ? (
                                    <button 
                                        onClick={() => navigate(`/student/course/${selectedCourse.courseId}`)}
                                        className="btn-open-course"
                                        title = 'Open Course'
                                    >
                                        📖 Open Course
                                    </button>
                                ) : (
                                    <button 
                                        onClick={() => {
                                            handleAddToCart(selectedCourse);
                                            closeCourseModal();
                                        }}
                                        className="btn-add-cart"
                                        title = 'Add to Cart'
                                    >
                                        🛒 Add to Cart
                                    </button>
                                )}
                                <button onClick={closeCourseModal} className="close-button">✕</button>
                            </div>
                        </div>

                        <div className="modal-body modal-body-scrollable">
                            <div className="course-details-container">

                                {/* Course Image */}
                                <div className="cd_image_wrap">
                                    <img
                                        src={`/courseImages/${selectedCourse.title}.png`}
                                        alt={selectedCourse.title}
                                        className="cd_image"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                </div>

                                {/* Badges */}
                                <div className="cd_badges">
                                    <span className="course-id-badge">{selectedCourse.courseId}</span>
                                    <span className="course-category-tag">{selectedCourse.category}</span>
                                    <span className="course-level-badge">{selectedCourse.level}</span>
                                </div>

                                {/* Title + Short Description */}
                                <h2 className="cd_title">{selectedCourse.title}</h2>
                                <p className="cd_short_desc">{selectedCourse.shortDescription}</p>

                                {/* Stats Bar */}
                                <div className="cd_stats_bar">
                                    <div className="cd_stat">
                                        <span className="cd_stat_icon">⏱️</span>
                                        <div>
                                            <div className="cd_stat_label">Duration</div>
                                            <div className="cd_stat_value">{selectedCourse.duration || 'N/A'}</div>
                                        </div>
                                    </div>
                                    <div className="cd_stat">
                                        <span className="cd_stat_icon">🎓</span>
                                        <div>
                                            <div className="cd_stat_label">Credits</div>
                                            <div className="cd_stat_value">{selectedCourse.credits || 0}</div>
                                        </div>
                                    </div>
                                    <div className="cd_stat">
                                        <span className="cd_stat_icon">💰</span>
                                        <div>
                                            <div className="cd_stat_label">Price</div>
                                            <div className="cd_stat_value">
                                                {parseFloat(selectedCourse.price) === 0
                                                    ? 'Free'
                                                    : `${selectedCourse.price} ${selectedCourse.currency}`}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="cd_stat">
                                        <span className="cd_stat_icon">👨‍🏫</span>
                                        <div>
                                            <div className="cd_stat_label">Instructor</div>
                                            <div className="cd_stat_value">{selectedCourse.instructorName || 'TBA'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* About */}
                                <div className="cd_section">
                                    <h3 className="cd_section_title">📝 About This Course</h3>
                                    <p className="cd_long_desc">{selectedCourse.longDescription}</p>
                                </div>

                                {/* Lessons */}
                                {selectedCourse.lessons && selectedCourse.lessons.length > 0 && (
                                    <div className="cd_section">
                                        <h3 className="cd_section_title">📚 Lessons ({selectedCourse.lessons.length})</h3>
                                        <div className="cd_lessons_list">
                                            {[...selectedCourse.lessons]
                                                .sort((a, b) => a.order - b.order)
                                                .map((lesson) => (
                                                    <div key={lesson.lessonId} className="cd_lesson_item">
                                                        <button
                                                            className="cd_lesson_header"
                                                            onClick={() => toggleLesson(lesson.lessonId)}
                                                        >
                                                            <span className="cd_lesson_order">{lesson.order}</span>
                                                            <span className="cd_lesson_title">{lesson.title}</span>
                                                            <span className="cd_lesson_chevron">
                                                                {expandedLessons[lesson.lessonId] ? '▲' : '▼'}
                                                            </span>
                                                        </button>
                                                        {expandedLessons[lesson.lessonId] && (
                                                            <div className="cd_lesson_content">
                                                                {lesson.content || 'No content available.'}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                )}

                                {/* Prerequisites */}
                                {selectedCourse.prerequisites && selectedCourse.prerequisites.length > 0 && (
                                    <div className="cd_section">
                                        <h3 className="cd_section_title">📋 Prerequisites</h3>
                                        <div className="cd_tags">
                                            {selectedCourse.prerequisites.map((prereq, idx) => (
                                                <span key={idx} className="cd_prereq_tag">{prereq}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Payment */}
                                <div className="cd_section">
                                    <h3 className="cd_section_title">💳 Payment Options</h3>
                                    <div className="cd_payment_grid">
                                        <div className="cd_payment_item">
                                            <span className="cd_payment_label">Full Price</span>
                                            <span className="cd_payment_value">
                                                {parseFloat(selectedCourse.price) === 0
                                                    ? 'Free'
                                                    : `${selectedCourse.price} ${selectedCourse.currency} / year`}
                                            </span>
                                        </div>

                                        {selectedCourse.paymentOptions?.allowInstallments &&
                                            selectedCourse.paymentOptions?.installmentPlans?.map((plan, idx) => {
                                                const price = parseFloat(selectedCourse.price);
                                                const currency = selectedCourse.currency;
                                                const planConfig = {
                                                    monthly_3:  { label: '3 Monthly Payments',  monthly: (price / 3  * 1.05).toFixed(2) },
                                                    monthly_6:  { label: '6 Monthly Payments',  monthly: (price / 6  * 1.15).toFixed(2) },
                                                    monthly_12: { label: '12 Monthly Payments', monthly: (price / 12 * 1.25).toFixed(2) },
                                                };
                                                const config = planConfig[plan];
                                                if (!config) return null;
                                                return (
                                                    <div key={idx} className="cd_payment_item">
                                                        <span className="cd_payment_label">{config.label}</span>
                                                        <span className="cd_payment_value">{currency} {config.monthly} / month</span>
                                                    </div>
                                                );
                                            })
                                        }

                                        {selectedCourse.paymentOptions?.refundWindowDays && (
                                            <div className="cd_payment_item">
                                                <span className="cd_payment_label">Refund Policy</span>
                                                <span className="cd_payment_value">
                                                    {selectedCourse.paymentOptions.refundWindowDays}-day refund window
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>
                        
                    </div>
                </div>
            )}

            <ProfIXChatbot userName={authData.name} />
        </div>
    );
};

export default Courses;
